import { useState, useEffect } from 'react';
import { supabase } from '@cleanpro/common-ui';

export interface StateStats {
  state: string;
  total: number;
  active: number;
  free: number;
  starter: number;
  steady: number;
  growth: number;
  avgRating: number | null;
}

export interface CityStats {
  city: string;
  state: string;
  total: number;
  active: number;
  free: number;
  starter: number;
  steady: number;
  growth: number;
  avgRating: number | null;
}

// Raw data from database uses different naming convention
interface RawStateStats {
  state: string;
  count: number;
  activeCount: number;
  freePlanCount: number;
  starterPlanCount: number;
  steadyPlanCount: number;
  growthPlanCount: number;
  avgRating: number | null;
}

interface RawCityStats {
  city: string;
  state: string;
  count: number;
  activeCount: number;
  freePlanCount: number;
  starterPlanCount: number;
  steadyPlanCount: number;
  growthPlanCount: number;
  avgRating: number | null;
}

export interface BusinessMetrics {
  totalBusinesses: number;
  activeBusinesses: number;
  inactiveBusinesses: number;
  freePlanCount: number;
  starterPlanCount: number;
  steadyPlanCount: number;
  growthPlanCount: number;
  avgRating: number | null;
  totalCompletedJobs: number;
  businessesWithInsurance: number;
  businessesByState: StateStats[];
  businessesByCity: CityStats[];
}

interface RawBusinessStats {
  total_businesses: number;
  active_businesses: number;
  inactive_businesses: number;
  free_plan_count: number;
  starter_plan_count: number;
  steady_plan_count: number;
  growth_plan_count: number;
  avg_rating: number | null;
  total_completed_jobs: number;
  businesses_with_insurance: number;
  businesses_by_state: RawStateStats[] | null;
  businesses_by_city: RawCityStats[] | null;
}

// Transform raw database format to application format
function transformStateStats(raw: RawStateStats[]): StateStats[] {
  return raw.map(stat => ({
    state: stat.state,
    total: stat.count,
    active: stat.activeCount,
    free: stat.freePlanCount,
    starter: stat.starterPlanCount,
    steady: stat.steadyPlanCount,
    growth: stat.growthPlanCount,
    avgRating: stat.avgRating,
  }));
}

function transformCityStats(raw: RawCityStats[]): CityStats[] {
  return raw.map(stat => ({
    city: stat.city,
    state: stat.state,
    total: stat.count,
    active: stat.activeCount,
    free: stat.freePlanCount,
    starter: stat.starterPlanCount,
    steady: stat.steadyPlanCount,
    growth: stat.growthPlanCount,
    avgRating: stat.avgRating,
  }));
}

/**
 * Hook to fetch and manage business/service provider metrics
 * Follows the pattern of useWorkflowMetrics with real-time updates
 */
export function useBusinessMetrics() {
  const [metrics, setMetrics] = useState<BusinessMetrics>({
    totalBusinesses: 0,
    activeBusinesses: 0,
    inactiveBusinesses: 0,
    freePlanCount: 0,
    starterPlanCount: 0,
    steadyPlanCount: 0,
    growthPlanCount: 0,
    avgRating: null,
    totalCompletedJobs: 0,
    businessesWithInsurance: 0,
    businessesByState: [],
    businessesByCity: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: rpcError } = await supabase
        .rpc('get_business_stats')
        .single();

      if (rpcError) {
        throw new Error(`Failed to fetch business stats: ${rpcError.message}`);
      }

      if (!data) {
        throw new Error('No data returned from get_business_stats');
      }

      const rawData = data as unknown as RawBusinessStats;

      // Debug logging
      console.log('[useBusinessMetrics] Raw data received:', {
        hasStateData: !!rawData.businesses_by_state,
        stateCount: rawData.businesses_by_state?.length || 0,
        hasCityData: !!rawData.businesses_by_city,
        cityCount: rawData.businesses_by_city?.length || 0,
      });

      // Transform snake_case to camelCase
      const metrics = {
        totalBusinesses: rawData.total_businesses || 0,
        activeBusinesses: rawData.active_businesses || 0,
        inactiveBusinesses: rawData.inactive_businesses || 0,
        freePlanCount: rawData.free_plan_count || 0,
        starterPlanCount: rawData.starter_plan_count || 0,
        steadyPlanCount: rawData.steady_plan_count || 0,
        growthPlanCount: rawData.growth_plan_count || 0,
        avgRating: rawData.avg_rating,
        totalCompletedJobs: Number(rawData.total_completed_jobs) || 0,
        businessesWithInsurance: rawData.businesses_with_insurance || 0,
        businessesByState: rawData.businesses_by_state 
          ? transformStateStats(rawData.businesses_by_state) 
          : [],
        businessesByCity: rawData.businesses_by_city 
          ? transformCityStats(rawData.businesses_by_city) 
          : [],
      };

      console.log('[useBusinessMetrics] Transformed metrics:', {
        stateCount: metrics.businessesByState.length,
        cityCount: metrics.businessesByCity.length,
        firstState: metrics.businessesByState[0],
      });

      setMetrics(metrics);
    } catch (err) {
      console.error('[useBusinessMetrics] Error fetching metrics:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();

    // Subscribe to changes in businesses table for real-time updates
    const subscription = supabase
      .channel('business-metrics-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'businesses',
        },
        () => {
          console.log('[useBusinessMetrics] Business change detected, refreshing metrics');
          fetchMetrics();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return {
    metrics,
    loading,
    error,
    refresh: fetchMetrics,
  };
}
